/**
 * Phase 46 (Plan 46-07) — REST client for /api/admin/notifications-audit.
 *
 * Cursor protocol (backend `notificationAuditController.ts`):
 *   - `cursor` query param is a base64-encoded JSON of `{ dispatchedAt, id }`.
 *   - Response `nextCursor` is `{ encoded, dispatchedAt, id } | null`.
 *   - Clients (TanStack useInfiniteQuery) feed `encoded` back into the next
 *     page's `cursor` query param.
 */
import { apiGet } from './client';
import type {
  Channel,
  EventKind,
  DispatchStatus,
} from '../../constants/notificationEnums';

// ---------------------------------------------------------------------------
// DTO types — mirror backend `NotificationDispatchAuditDTO` verbatim
// ---------------------------------------------------------------------------

export interface NotificationDispatchAuditDTO {
  id: number;
  stateChangeId: number;
  // Phase 103 (D-01): nullable — kudos, training and bot-usage are
  // project-less producer families and write projectId = null.
  projectId: number | null;
  recipientId: number | null;
  channel: Channel;
  target: string | null;
  status: DispatchStatus;
  skipReason: string | null;
  errorMessage: string | null;
  dispatchedAt: string;
  // Phase 103 (D-02): denormalized event kind, mirrors the backend column.
  // Nullable — pre-Phase-103 rows are backfilled; a future producer that
  // misses a call site would surface as a visible null here.
  kind: EventKind | null;
}

export interface AuditListFilters {
  projectId?: number;
  eventKind?: EventKind;
  channel?: Channel;
  status?: DispatchStatus;
  recipientId?: number;
  from?: string; // ISO date-time
  to?: string; // ISO date-time
}

export interface AuditNextCursor {
  encoded: string;
  dispatchedAt: string;
  id: number;
}

export interface AuditListResponse {
  data: NotificationDispatchAuditDTO[];
  nextCursor: AuditNextCursor | null;
}

export type NotificationDispatchAuditDetailDTO = NotificationDispatchAuditDTO & {
  stateChangePayload: Record<string, unknown> | null;
};

// ---------------------------------------------------------------------------
// Querystring helper
// ---------------------------------------------------------------------------

function buildAuditQuery(
  filters: AuditListFilters,
  cursor: string | null,
  limit?: number,
): string {
  const parts: string[] = [];
  if (filters.projectId !== undefined) {
    parts.push(`project_id=${encodeURIComponent(String(filters.projectId))}`);
  }
  if (filters.eventKind !== undefined) {
    parts.push(`event_kind=${encodeURIComponent(filters.eventKind)}`);
  }
  if (filters.channel !== undefined) {
    parts.push(`channel=${encodeURIComponent(filters.channel)}`);
  }
  if (filters.status !== undefined) {
    parts.push(`status=${encodeURIComponent(filters.status)}`);
  }
  if (filters.recipientId !== undefined) {
    parts.push(`recipient_id=${encodeURIComponent(String(filters.recipientId))}`);
  }
  if (filters.from !== undefined) {
    parts.push(`from=${encodeURIComponent(filters.from)}`);
  }
  if (filters.to !== undefined) {
    parts.push(`to=${encodeURIComponent(filters.to)}`);
  }
  if (cursor) {
    parts.push(`cursor=${encodeURIComponent(cursor)}`);
  }
  if (limit !== undefined) {
    parts.push(`limit=${encodeURIComponent(String(limit))}`);
  }
  return parts.length > 0 ? `?${parts.join('&')}` : '';
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

export async function fetchNotificationDispatchAudit(
  filters: AuditListFilters,
  cursor: string | null,
  limit?: number,
): Promise<AuditListResponse> {
  const qs = buildAuditQuery(filters, cursor, limit);
  return apiGet<AuditListResponse>(`/admin/notifications-audit${qs}`);
}

export async function fetchNotificationDispatchAuditById(
  id: number,
): Promise<NotificationDispatchAuditDetailDTO> {
  const res = await apiGet<{ data: NotificationDispatchAuditDetailDTO }>(
    `/admin/notifications-audit/${id}`,
  );
  return res.data;
}
